import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, type RouteObject, RouterProvider } from 'react-router-dom';

import { AdminLayout } from '@/components/layout/AdminLayout';
import { RequireAuth } from '@/components/RequireAuth';
import { RequirePermission } from '@/components/RequirePermission';
import { AuthProvider } from '@/lib/auth/AuthProvider';
import { EditorLocaleProvider } from '@/lib/editorLocale/EditorLocaleProvider';
import { NAV_ITEMS } from '@/lib/nav/navigation';
import { AuthLayout } from '@/routes/auth/AuthLayout';
import { ForgotPasswordPage } from '@/routes/auth/ForgotPasswordPage';
import { LoginPage } from '@/routes/auth/LoginPage';
import { ResetPasswordPage } from '@/routes/auth/ResetPasswordPage';
import { TwoFactorChallengePage } from '@/routes/auth/TwoFactorChallengePage';
import { TwoFactorSetupPage } from '@/routes/auth/TwoFactorSetupPage';
import { DashboardPage } from '@/routes/DashboardPage';
import { MODULE_STEPS } from '@/routes/moduleSteps';
import { ModulePlaceholder } from '@/routes/ModulePlaceholder';
import { ProfilePage } from '@/routes/system/ProfilePage';
import { DepartmentsPage } from '@/routes/academic/DepartmentsPage';
import { FacultiesPage } from '@/routes/academic/FacultiesPage';
import { ProgramsPage } from '@/routes/academic/ProgramsPage';
import { AnnouncementsPage } from '@/routes/content/AnnouncementsPage';
import { ClubsPage } from '@/routes/content/ClubsPage';
import { EventsPage } from '@/routes/content/EventsPage';
import { ProjectsPage } from '@/routes/content/ProjectsPage';
import { VacanciesPage } from '@/routes/content/VacanciesPage';
import { DocumentCategoriesPage } from '@/routes/documents/DocumentCategoriesPage';
import { DocumentsPage } from '@/routes/documents/DocumentsPage';
import { MediaPage } from '@/routes/media/MediaPage';
import { NewsCategoriesPage } from '@/routes/news/NewsCategoriesPage';
import { NewsPage } from '@/routes/news/NewsPage';
import { TagsPage } from '@/routes/news/TagsPage';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { MenusPage } from '@/routes/navigation/MenusPage';
import { RedirectsPage } from '@/routes/operations/RedirectsPage';
import { SubmissionsPage } from '@/routes/operations/SubmissionsPage';
import { SettingsPage } from '@/routes/navigation/SettingsPage';
import { UsefulLinksPage } from '@/routes/navigation/UsefulLinksPage';
import { ModalsPage } from '@/routes/pages/ModalsPage';
import { PageSectionsPage } from '@/routes/pages/PageSectionsPage';
import { PagesPage } from '@/routes/pages/PagesPage';
import { StaffPage } from '@/routes/staff/StaffPage';
import { ActivityPage } from '@/routes/system/ActivityPage';
import { MigrationPage } from '@/routes/system/migration/MigrationPage';
import { RolesPage } from '@/routes/system/RolesPage';
import { UsersPage } from '@/routes/system/UsersPage';
import { VocationalContactsPage } from '@/routes/vocational/VocationalContactsPage';
import { VocationalProgramsPage } from '@/routes/vocational/VocationalProgramsPage';

/**
 * docs/06-FRONTEND-SPEC.md §3 — the admin has different cache needs from the
 * public site: an editor who has just saved must see their own change, so
 * admin data is refetched on focus and is not held stale.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      /*
       * A 401 is not worth retrying — the token is dead and the second attempt
       * fails identically, while the re-auth modal (§6.7) has already been
       * raised by the first.
       */
      retry: (failureCount, error) =>
        failureCount < 1 && !(error instanceof Error && error.name === 'ApiRequestError'),
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * A `<Outlet>`-shaped wrapper so every auth screen shares the centred card
 * without each of them importing and titling it.
 */
function AuthScreen({ screen }: { screen: 'login' | 'twoFactor' | 'setup' | 'forgot' | 'reset' }) {
  const { t } = useTranslation();

  const children = {
    login: <LoginPage />,
    twoFactor: <TwoFactorChallengePage />,
    setup: <TwoFactorSetupPage />,
    forgot: <ForgotPasswordPage />,
    reset: <ResetPasswordPage />,
  }[screen];

  // `reset` has no sub-line of its own — the account it is for is shown in the
  // form, where it is closer to the fields it explains.
  const description = screen === 'reset' ? undefined : t(`auth.${screen}.description`);

  return (
    <AuthLayout title={t(`auth.${screen}.title`)} description={description}>
      {children}
    </AuthLayout>
  );
}

/**
 * §2's route table, built from the same data the sidebar and the palette read.
 *
 * Generated rather than written out so the three cannot disagree: a screen with
 * no route is a dead sidebar entry, and a route with no table row is a screen
 * with no permission on it. The modules replace their placeholder as they land.
 */
/**
 * The screens that exist. Everything else in the table renders its placeholder
 * until its step lands, and an entry disappears from here as it is built.
 */
const BUILT: Record<string, React.ReactElement> = {
  news: <NewsPage />,
  announcements: <AnnouncementsPage />,
  events: <EventsPage />,
  clubs: <ClubsPage />,
  projects: <ProjectsPage />,
  vacancies: <VacanciesPage />,
  faculties: <FacultiesPage />,
  departments: <DepartmentsPage />,
  programs: <ProgramsPage />,
  staff: <StaffPage />,
  pages: <PagesPage />,
  pageSections: <PageSectionsPage />,
  modals: <ModalsPage />,
  menus: <MenusPage />,
  usefulLinks: <UsefulLinksPage />,
  settings: <SettingsPage />,
  submissions: <SubmissionsPage />,
  redirects: <RedirectsPage />,
  users: <UsersPage />,
  roles: <RolesPage />,
  activity: <ActivityPage />,
  migration: <MigrationPage />,
  profile: <ProfilePage />,
  documents: <DocumentsPage />,
  media: <MediaPage />,
  documentCategories: <DocumentCategoriesPage />,
  newsCategories: <NewsCategoriesPage />,
  tags: <TagsPage />,
  vocationalContacts: <VocationalContactsPage />,
  vocationalPrograms: <VocationalProgramsPage />,
};

function moduleRoutes(): RouteObject[] {
  return NAV_ITEMS.filter((item) => item.path !== '/').map((item) => ({
    element: (
      <RequirePermission permission={item.permission} superAdminOnly={item.superAdminOnly} />
    ),
    children: [
      {
        // `/*` so a detail route inside a module — `/news/12`, `/settings/seo`
        // — is still gated by the module's permission before its own screen
        // decides what to do with the rest of the path.
        path: `${item.path.replace(/^\//, '')}/*`,
        element: BUILT[item.key] ?? (
          <ModulePlaceholder navKey={item.key} step={MODULE_STEPS[item.key] ?? 0} />
        ),
      },
      {
        path: item.path.replace(/^\//, ''),
        element: BUILT[item.key] ?? (
          <ModulePlaceholder navKey={item.key} step={MODULE_STEPS[item.key] ?? 0} />
        ),
      },
    ],
  }));
}

const router = createBrowserRouter([
  { path: '/login', element: <AuthScreen screen="login" /> },
  { path: '/login/two-factor', element: <AuthScreen screen="twoFactor" /> },
  { path: '/login/setup', element: <AuthScreen screen="setup" /> },
  { path: '/forgot-password', element: <AuthScreen screen="forgot" /> },
  { path: '/reset-password', element: <AuthScreen screen="reset" /> },

  {
    path: '/',
    element: <RequireAuth />,
    // Anything that throws on the way to a screen renders here rather than as
    // React Router's own English stack trace.
    errorElement: <NotFoundPage />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <DashboardPage /> },

          ...moduleRoutes(),

          /*
           * Everything else. It sits *inside* the guard on purpose: a stale
           * bookmark to a record should ask an anonymous visitor to sign in
           * before telling them whether that record exists.
           */
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Inside the query provider: signing out clears the cache, so the
          provider has to exist by the time the session does. */}
      <AuthProvider>
        <EditorLocaleProvider>
          <RouterProvider router={router} />
        </EditorLocaleProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
